import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Building2, CalendarOff, ChevronLeft, Globe, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import client from "@/lib/api";
import Brand from "@/components/Brand";
import { HonorBoard } from "@/components/HonorBoard";

/**
 * ModuleChooser — post-login landing page that lets the user pick which
 * tracker to work with. Stores the selection in localStorage so the drawer
 * can highlight the active tracker & offer a quick switch.
 */
export default function ModuleChooser() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const [enabledModules, setEnabledModules] = useState(null);
  const [notifSummary, setNotifSummary] = useState({});

  useEffect(() => {
    client.get("/settings/public").then((r) => setEnabledModules(r.data?.modules || null)).catch(() => {});
    client.get("/notifications/summary").then((r) => setNotifSummary(r.data || {})).catch(() => {});
  }, []);

  const choose = (key) => {
    localStorage.setItem("hct_module", key);
    if (key === "licenses") nav("/licenses");
    else if (key === "dayoff") nav("/dayoff");
    else if (key === "empmgmt") nav("/employee-management");
    else nav("/");
  };

  const cards = [
    {
      key: "empmgmt",
      title: t("chooser_module_empmgmt_title"),
      subtitle: t("chooser_module_empmgmt_subtitle"),
      icon: Users,
      color: "from-sky-500 to-blue-600",
      accent: "bg-sky-500",
    },
    {
      key: "employees",
      title: t("chooser_module_employees_title"),
      subtitle: t("chooser_module_employees_subtitle"),
      icon: ShieldCheck,
      color: "from-teal-500 to-emerald-600",
      accent: "bg-teal-500",
    },
    {
      key: "dayoff",
      title: "Day off MenaMe",
      subtitle: t("chooser_module_dayoff_subtitle"),
      icon: CalendarOff,
      color: "from-amber-500 to-orange-600",
      accent: "bg-amber-500",
    },
    {
      key: "licenses",
      title: t("chooser_module_licenses_title"),
      subtitle: t("chooser_module_licenses_subtitle"),
      icon: Building2,
      color: "from-indigo-500 to-fuchsia-600",
      accent: "bg-fuchsia-500",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-fuchsia-50/20 flex flex-col">
      <header className="bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <Brand />
        <div className="flex items-center gap-3 text-xs">
          {user && (
            <span className="text-slate-600">
              👋 <b>{user.full_name || user.username}</b>
            </span>
          )}
          <button
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="inline-flex items-center gap-1.5 border border-teal-200 rounded-full px-2.5 py-1 text-xs font-bold text-teal-800 hover:bg-teal-50 hover:border-teal-400 transition"
            data-testid="lang-toggle-chooser"
            title={t("language")}
          >
            <Globe size={12} /> {lang === "ar" ? "English" : "عربي"}
          </button>
          <button
            onClick={logout}
            className="text-rose-600 hover:text-rose-700 font-semibold"
            data-testid="chooser-logout"
          >
            {t("chooser_logout")}
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-5">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-black text-slate-800 mb-2">
              {t("chooser_welcome")}
            </h1>
            <p className="text-sm text-slate-500">
              {t("chooser_hint")}
            </p>
          </div>

          <div className="mb-6">
            {user?.role !== "branch_manager" && <HonorBoard />}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="module-chooser">
            {cards.filter((c) => (!enabledModules || enabledModules[c.key] !== false) && (user?.role !== "branch_manager" || user?.modules?.[c.key])).map((c) => (
              <button
                key={c.key}
                onClick={() => choose(c.key)}
                className="group relative overflow-hidden text-start rounded-2xl bg-white border-2 border-slate-200 hover:border-teal-400 shadow-sm hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                data-testid={`choose-${c.key}`}
              >
                {(notifSummary[c.key] || 0) > 0 && (
                  <span
                    className="absolute top-3 end-3 z-10 min-w-[24px] px-2 py-1 rounded-full bg-rose-500 text-white text-[11px] font-black text-center shadow"
                    data-testid={`chooser-notif-badge-${c.key}`}
                  >
                    {notifSummary[c.key] > 99 ? "99+" : notifSummary[c.key]}
                  </span>
                )}
                <div className={`h-1.5 bg-gradient-to-l ${c.color}`} />
                <div className="p-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${c.color} text-white shadow-lg mb-4 group-hover:scale-110 transition-transform`}>
                    <c.icon size={32} />
                  </div>
                  <h3 className="text-2xl font-black text-slate-800 mb-2">{c.title}</h3>
                  <p className="text-sm text-slate-500 mb-6">{c.subtitle}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">{t("chooser_click_enter")}</span>
                    <ChevronLeft size={18} className="text-teal-700 group-hover:-translate-x-1 transition-transform" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
